import React, { useEffect, useRef } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, RefreshCcw } from "lucide-react";

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

const stopHtml5 = async (instance) => {
  if (!instance) return;
  try {
    if (typeof instance.getState === "function" && instance.getState() === 2) {
      await instance.stop();
    }
    await instance.clear();
  } catch (e) { /* noop */ }
};

const pickCamera = async () => {
  if (!isIOS()) return { facingMode: "environment" };
  try {
    const cams = await Html5Qrcode.getCameras();
    if (cams && cams.length) {
      const rear = cams.find((c) => /back|rear|environment/i.test(c.label));
      return (rear || cams[cams.length - 1]).id;
    }
  } catch (e) { /* fall back below */ }
  return { facingMode: "environment" };
};

// Build and start a scanner attached to `container`. Returns the instance (so caller can store/stop it).
const buildAndStart = async (container, onDecode) => {
  const id = "barcode-reader-region";
  if (!container) return null;
  container.id = id;
  const html5 = new Html5Qrcode(id, { verbose: false });
  const cameraConfig = await pickCamera();
  const config = { fps: 12, qrbox: { width: 260, height: 140 } };
  try {
    await html5.start(
      cameraConfig,
      config,
      (decoded) => {
        onDecode(decoded);
        stopHtml5(html5);
      },
      () => { /* scan errors ignored */ }
    );
  } catch (e) {
    console.warn("Scanner start failed:", e?.message);
  }
  return html5;
};

export default function BarcodeScanner({ onResult, onClose }) {
  const containerRef = useRef(null);
  const scannerRef = useRef(null);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;

  useEffect(() => {
    let active = true;
    (async () => {
      const inst = await buildAndStart(containerRef.current, (code) => onResultRef.current?.(code));
      if (active) {
        scannerRef.current = inst;
      } else {
        stopHtml5(inst);
      }
    })();
    return () => {
      active = false;
      const inst = scannerRef.current;
      scannerRef.current = null;
      stopHtml5(inst);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRestart = async () => {
    const prev = scannerRef.current;
    scannerRef.current = null;
    await stopHtml5(prev);
    const inst = await buildAndStart(containerRef.current, (code) => onResultRef.current?.(code));
    scannerRef.current = inst;
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="scanner-overlay">
      <div className="brut-card w-full max-w-md bg-white">
        <div className="flex items-center justify-between border-b-2 border-black px-4 py-3">
          <div className="flex items-center gap-2 font-heading font-black text-lg">
            <Camera strokeWidth={3} size={18} /> SCAN BARCODE
          </div>
          <button onClick={onClose} className="brut-btn brut-btn-secondary !py-1 !px-2" data-testid="scanner-close-btn">
            <X size={16} strokeWidth={3} />
          </button>
        </div>
        <div className="p-4">
          <div ref={containerRef} className="scan-frame w-full aspect-[4/3] overflow-hidden bg-black" />
          <button
            onClick={handleRestart}
            className="brut-btn brut-btn-secondary w-full mt-3 text-xs"
            data-testid="scanner-restart-btn"
          >
            <RefreshCcw size={14} strokeWidth={3} /> RESTART CAMERA
          </button>
          <div className="mt-3 text-xs uppercase tracking-wider text-[#666]">
            ▸ iPhone users: if preview is black, tap RESTART CAMERA and allow access.
          </div>
        </div>
      </div>
    </div>
  );
}
